import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { type NotificationDto, type PagedResult, type QueryOptions } from '../lib/types';

const KEYS = {
  list: (opts: QueryOptions, unreadOnly: boolean) => ['notifications', 'list', opts, unreadOnly] as const,
  unreadCount: ['notifications', 'unreadCount'] as const,
};

/** Polls the server every 60s for an updated unread count; refetches on window focus. */
export function useUnreadCount() {
  return useQuery({
    queryKey: KEYS.unreadCount,
    queryFn: () => apiClient.get<number>('/api/notifications/unread-count'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

export function useNotifications(options: QueryOptions, unreadOnly = false) {
  const params = new URLSearchParams();
  if (options.page) params.set('page', String(options.page));
  if (options.pageSize) params.set('pageSize', String(options.pageSize));
  if (unreadOnly) params.set('unreadOnly', 'true');
  const qs = params.toString() ? `?${params}` : '';

  return useQuery({
    queryKey: KEYS.list(options, unreadOnly),
    queryFn: () => apiClient.get<PagedResult<NotificationDto>>(`/api/notifications${qs}`),
    staleTime: 15_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.post<void>(`/api/notifications/${id}/read`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<number>('/api/notifications/read-all', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
